import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import { Button, Modal } from '@resurs/midas-react'
import { getText } from '../../../../internal/TextLabels'
import { ApplicationStatus } from '../../../../models/application-status'
import {
  ApplicantType,
  TaskStatus,
  TaskType,
} from '../../../../models/flow-service'
import { AuthContext } from '../../../../context/AuthContext'
import { actOnData } from '../../../../util/helpers'
import { useToggle } from '../../../../util/utility-hooks'
import RetryDigitalAgreementSigningModal from './RetryDigitalAgreementSigningModal'
import styles from './FlowModalUI.module.scss'

const getTaskDescription = (taskType, canceled, currentStatus) => {
  let title = ''
  let description = ''
  switch (taskType) {
    case TaskType.AUTHENTICATION:
    case currentStatus === ApplicationStatus.AUTHENTICATION_REQUIRED &&
      TaskType.CUSTOMER_LANDING:
      title = getText('payApply.identifying.title')
      description = canceled
        ? getText('payApply.identifying.abort')
        : getText('payApply.identifying.descr')
      break
    case TaskType.SIGNING:
    case currentStatus === ApplicationStatus.SIGNING_REQUIRED &&
      TaskType.CUSTOMER_LANDING:
      title = getText('payApply.signing.title')
      description = canceled
        ? getText('payApply.signing.abort')
        : getText('payApply.signing.descr')
      break
    case TaskType.CREDIT_CONSENT:
    case TaskType.CREDIT_STATUS_ESKAT:
    case TaskType.MARKETING_CONSENT:
      title = getText('payApply.consent.title')
      description = canceled
        ? getText('payApply.consent.abort')
        : getText('payApply.consent.descr')
      break
    case TaskType.PSP:
    default:
      break
  }
  return {
    title,
    description,
  }
}

const getProcessingDescription = processing => ({
  title: processing ? getText('payApply.decision.title') : '',
  text: '',
})

const FlowModalUI = ({
  onClose,
  onComplete,
  data,
  customCancelComponent: CancelComponent,
  ...props
}) => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [completed, toggleCompleted] = useToggle(false)
  const [canceled, setCanceled] = useState(false)
  const [retryDigitalAgreementSigning, toggleRetryDigitalAgreementSigning] =
    useToggle(false)
  const [event, setEvent] = useState(null)
  const storeEvent = useRef()
  const { token } = useContext(AuthContext)

  const saveEventToState = useCallback(eventData => {
    setEvent(eventData)
  }, [])

  const updateFlowStatus = ({ title: _title, description: _description }) => {
    setTitle(_title)
    setDescription(_description)
  }

  const getCurrentTask = tasks => {
    const verificationTasks = tasks
      .filter(task => !!task)
      .filter(
        ({ taskType, status }) =>
          taskType === TaskType.DIGITAL_AGREEMENT_SIGNING &&
          status === TaskStatus.AWAITING_VERIFICATION,
      )
    if (verificationTasks.length) {
      return verificationTasks[0]
    }
    return (
      tasks.filter(
        ({ applicantType }) => applicantType === ApplicantType.MAIN_APPLICANT,
      )[0] || tasks[0]
    )
  }

  const handleTaskTypeUpdate = tasks => {
    const currentTask = getCurrentTask(tasks)
    const isCanceled = tasks.some(
      ({ status }) => status === TaskStatus.CANCELED,
    )

    const isDeclined = tasks.some(
      ({ status }) => status === TaskStatus.DECLINED,
    )

    if (isCanceled) {
      setCanceled(true)
    } else if (canceled) {
      setCanceled(false)
    }

    if (currentTask.taskType === TaskType.DIGITAL_AGREEMENT_SIGNING) {
      if (isCanceled || retryDigitalAgreementSigning || isDeclined) {
        toggleRetryDigitalAgreementSigning()
        return
      }
    }

    if (currentTask.taskType) {
      updateFlowStatus(
        getTaskDescription(
          currentTask.taskType,
          isCanceled,
          data.current?.status,
        ),
      )
    }
  }

  const handleFlowComplete = () => {
    if (canceled) {
      return
    }
    updateFlowStatus({
      title: getText('payApply.decision.title'),
      description: getText('payApply.decision.descr'),
    })
    toggleCompleted()
    onComplete()
  }

  const updateIframeWithToken = () => {
    if (storeEvent.current && storeEvent.current.contentWindow) {
      storeEvent.current.contentWindow.postMessage(
        { type: 'mp-token', value: token },
        '*',
      )
    }
  }

  useEffect(() => {
    if (event && event.data && event.data.src === 'flow-ui') {
      switch (event.data.type) {
        case 'close':
          handleFlowComplete()
          break
        case 'current_task_types':
          handleTaskTypeUpdate(event.data.value || [])
          break
        case 'token_please':
          updateIframeWithToken()
          break
        case 'processing':
          updateFlowStatus(getProcessingDescription(event.data.value))
          break
        default:
          break
      }
    }
  }, [event])

  useEffect(() => {
    const handleMessage = e => {
      if (e && e.data && e.data.src === 'flow-ui') {
        saveEventToState({ data: e.data })
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const modalProps = {
    title,
    onClose,
    actions: canceled ? (
      <Button type="primary" kind="danger" size="small" onClick={onClose}>
        {getText('default.close')}
      </Button>
    ) : !completed ? (
      (CancelComponent && <CancelComponent onClose={onClose} />) || (
        <Button
          id="digital-cancel-btn"
          type="normal"
          badgeLabel="danger"
          size="small"
          onClick={onClose}
        >
          {getText('default.cancel')}
        </Button>
      )
    ) : null,
    hideClose: true,
  }

  return (
    <div className={styles.modalContainer}>
      <RetryDigitalAgreementSigningModal
        {...modalProps}
        show={!!(props.digitalAgreementOnNext && retryDigitalAgreementSigning)}
        onNext={e => {
          props
            .digitalAgreementOnNext(e)
            .then(() => toggleRetryDigitalAgreementSigning())
        }}
        customer={props.customer}
        coCustomer={props.coCustomer}
        isCanceled={canceled}
      />
      {!retryDigitalAgreementSigning &&
        actOnData(
          data.redirectionResult,
          ({ salesPerson: { url: flowUrl } }) => {
            return (
              <Modal show {...modalProps}>
                <div className={styles.iframeContainer}>
                  <iframe
                    title="Applicant flow status"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-top-navigation allow-top-navigation-by-user-activation"
                    ref={storeEvent}
                    src={flowUrl}
                  />
                </div>
                {actOnData(description, text => (
                  <p
                    style={{
                      textAlign: 'center',
                      marginBottom: '3rem',
                    }}
                  >
                    {text}
                  </p>
                ))}
              </Modal>
            )
          },
        )}
    </div>
  )
}

FlowModalUI.propTypes = {
  data: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  digitalAgreementOnNext: PropTypes.func,
  customer: PropTypes.object,
  coCustomer: PropTypes.object,
  customCancelComponent: PropTypes.elementType,
}

FlowModalUI.defaultProps = {
  customer: undefined,
  coCustomer: undefined,
  digitalAgreementOnNext: undefined,
  customCancelComponent: undefined,
}

export default FlowModalUI
